import { useCallback, useEffect, useState } from "react";
import {
  enablePush,
  getCurrentSubscription,
  getPermission,
  isPushSupported,
  removePushSubscription,
  syncPushSubscription,
} from "../services/pushDevice";

const SYNC_FLAG = "push-synced";

// Estado do push neste aparelho. Ao montar, se a permissão já foi dada,
// reinscreve em segundo plano (uma vez por sessão) — assim quem fica logado
// por semanas, ou trocou de aparelho, continua recebendo.
export function usePushNotifications({ autoSync = true } = {}) {
  const [isSupported] = useState(isPushSupported);
  const [permission, setPermission] = useState(getPermission);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkSubscription = useCallback(async () => {
    if (!isSupported) return false;
    try {
      const subscription = await getCurrentSubscription();
      setIsSubscribed(!!subscription);
      return !!subscription;
    } catch {
      setIsSubscribed(false);
      return false;
    }
  }, [isSupported]);

  const registerPush = useCallback(async () => {
    try {
      const ok = await syncPushSubscription();
      setIsSubscribed(ok);
      return ok;
    } catch {
      return false;
    }
  }, []);

  // Precisa ser chamado a partir de um clique (exigência do navegador)
  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;
    setLoading(true);
    try {
      const result = await enablePush();
      setPermission(getPermission());
      const ok = result === "granted";
      setIsSubscribed(ok);
      if (ok) sessionStorage.setItem(SYNC_FLAG, "1");
      return ok;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    await removePushSubscription();
    setIsSubscribed(false);
  }, []);

  useEffect(() => {
    if (!isSupported) return;

    checkSubscription();

    if (!autoSync || Notification.permission !== "granted" || !localStorage.getItem("token")) return;

    let alreadySynced = false;
    try {
      alreadySynced = sessionStorage.getItem(SYNC_FLAG) === "1";
    } catch {
      // sessionStorage bloqueado: sincroniza mesmo assim
    }
    if (alreadySynced) return;

    registerPush().then((ok) => {
      if (ok) {
        try {
          sessionStorage.setItem(SYNC_FLAG, "1");
        } catch {
          // ignora
        }
      }
    });
  }, [isSupported, autoSync, checkSubscription, registerPush]);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    requestPermission,
    registerPush,
    unsubscribe,
    checkSubscription,
  };
}
